import {
  faBackward,
  faForward,
  faPause,
  faPlay,
  faUndoAlt,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import cx from 'classnames'
import { debounce } from 'lodash'
import React, { createRef, useEffect, MouseEvent, ChangeEvent, KeyboardEvent } from 'react'

import doSave from '../../actions/save'
import { togglePlay } from '../../actions/play-actions'
import loadNextTrack from '../../actions/track-loading/loadNextTrack'
import { useAppDispatch, useAppSelector } from '../../hooks'
import modalsSlice from '../../reducers/modalsSlice'
import settingsSlice from '../../reducers/settingsSlice'
import { selectCurrentTrackSlug, selectIsNeedSyncing } from '../../selectors/current-play-selectors'
import { getModeFlags, selectMode } from '../../selectors/session-selectors'
import { selectIsOptionKey } from '../../selectors/settings-selectors'
import trackMixer from '../../services/TrackMixer'
import { isMobile as getIsMobile } from '../../util/track-utils'
import { PlayActions } from '../../types'

type Props = {
  playActions: PlayActions
}
const DEBOUNCE_TIME_MS = 100

const PlayControls = ({ playActions }: Props) => {
  const isMobile = getIsMobile()
  const dispatch = useAppDispatch()
  const { rewindToStart, rewindTo, clear, forget } = playActions
  const { isEditMode } = getModeFlags(useAppSelector(selectMode))
  const isNeedSyncing = useAppSelector(selectIsNeedSyncing)
  const isTrackLoaded = !!useAppSelector(selectCurrentTrackSlug)
  const { trackDuration, isPlaying, clockStartAtSeconds, isScoreDirty } = useAppSelector(
    (state) => state.currentPlay
  )
  const doneCount = 0 //gamerStatus.length ? gamerStatus[0].doneCount : 0
  const hasTiming = doneCount > 0
  const isOptionKey = useAppSelector(selectIsOptionKey(0))

  /**
   * If option key is pressed, we want to clear it after 5 second.
   * Note this runs every time option key is pressed, ensure to noop if isOptionKey is already false
   * https://mondrian-dev.atlassian.net/browse/MON-123
   */
  useEffect(() => {
    // if the isOptionKey is not pressed do not set timeout
    if (!isOptionKey) return

    const timeoutId = setTimeout(() => {
      // option key may be false already when the timeout runs, we can skip
      if (!isOptionKey) return
      dispatch(settingsSlice.actions.setIsOptionKey({ isOptionKey: false }))
    }, 5000)
    return () => clearTimeout(timeoutId)
  }, [isOptionKey, dispatch])

  const onPlayClick = () => dispatch(togglePlay(false))
  const onRewindClick = () => rewindToStart()
  const onScrubberInput = React.useMemo(
    () =>
      debounce(
        (event: ChangeEvent<HTMLInputElement>) => {
          rewindTo(parseFloat(event.target.value))
        },
        DEBOUNCE_TIME_MS,
        { leading: true }
      ),
    [rewindTo]
  )
  useEffect(() => {
    return () => {
      onScrubberInput.cancel()
    }
  }, [onScrubberInput])
  const onClearClick = (event: MouseEvent) => (event.altKey ? forget() : clear())
  const onSaveClick = (event: MouseEvent) => {
    if (isEditMode && !event.altKey) {
      dispatch(modalsSlice.actions.toggleSyncModal(true))
    } else {
      dispatch(doSave({ isDownload: event.altKey, gamer: trackMixer.defaultGamer }))
    }
  }
  const onPrevClick = () => {
    dispatch(loadNextTrack({ isPrevious: true }))
  }
  const onNextClick = () => {
    dispatch(loadNextTrack({}))
  }
  const clearButtonClassName = cx({ dirty: hasTiming })
  const exportButtonClassName = cx({ dirty: isScoreDirty || isNeedSyncing, hidden: isMobile })
  const isExportButtonEnabled = isScoreDirty || isOptionKey || isEditMode
  const playText = isPlaying ? 'Pause' : 'Play'
  const playIcon = (
    <FontAwesomeIcon icon={(isPlaying ? faPause : faPlay) as IconProp} title={playText} />
  )
  // const clockText = '-:-:-';
  // const durationText = '-:-:-'; // TODO: support displaying this while track is loading
  const clockRef = createRef<HTMLInputElement>()
  React.useEffect(() => {
    const elem = clockRef.current
    if (elem) {
      trackMixer.clock = elem
    }
  })
  const scrubberRef = createRef<HTMLInputElement>()
  React.useEffect(() => {
    const elem = scrubberRef.current
    if (elem) {
      trackMixer.scrubber = elem
    }
  })
  const durationRef = createRef<HTMLInputElement>()
  React.useEffect(() => {
    const elem = durationRef.current
    if (elem) {
      trackMixer.duration = elem
    }
  })
  const getScrubber = () => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (scrubberRef.current) {
        const step = event.keyCode === 38 ? 0.2 : event.keyCode === 40 ? -0.2 : 0
        const newValue = parseFloat(scrubberRef.current.value) + step
        console.log('scrubbing to', newValue)
        scrubberRef.current.value = String(newValue)
        rewindTo(newValue)
      }
      event.stopPropagation()
    }
    return (
      <div>
        <input ref={clockRef} type="text" className="timer" readOnly tabIndex={-1} />
        <input
          ref={scrubberRef}
          className="scrubber"
          type="range"
          step="0.2"
          min="0"
          max={trackDuration}
          onChange={onScrubberInput}
          onInput={onScrubberInput}
          disabled={!isTrackLoaded}
          onKeyDown={onKeyDown}
        />
        <input ref={durationRef} type="text" className="timer" readOnly tabIndex={-1} />
      </div>
    )
  }
  return (
    <div className="playUi">
      {/*{(!isMobile || isLandscape) && (*/}
      {/*  <BlastButton isRight={false} playerName={buttonPlayerNames[0]} />*/}
      {/*)}*/}
      <div className="playControls">
        {getScrubber()}
        <div>
          <button onClick={onPrevClick} aria-label="Previous Track" disabled={!isTrackLoaded}>
            <FontAwesomeIcon icon={faBackward as IconProp} title="Previous Track" />
          </button>
          <button
            onClick={onRewindClick}
            aria-label="restart"
            disabled={!isPlaying && clockStartAtSeconds === 0}
          >
            <FontAwesomeIcon icon={faUndoAlt as IconProp} title="Rewind to Start" />
          </button>
          <button id="play" aria-label={playText} onClick={onPlayClick} disabled={!isTrackLoaded}>
            {playIcon}
          </button>
          <button onClick={onNextClick} aria-label="Next Track" disabled={!isTrackLoaded}>
            <FontAwesomeIcon icon={faForward as IconProp} title="Next Track" />
          </button>
          {!trackMixer.isVizMode && (
            <button
              className={exportButtonClassName}
              onClick={onSaveClick}
              disabled={!isExportButtonEnabled}
            >
              {isOptionKey ? 'Export' : isEditMode ? 'Sync' : 'Save'}
            </button>
          )}
          {!trackMixer.isVizMode && (
            <button className={clearButtonClassName} onClick={onClearClick}>
              {isOptionKey ? 'Forget' : 'Clear'}
            </button>
          )}
        </div>
      </div>
      {/*{(!isMobile || isLandscape) && <BlastButton isRight playerName={buttonPlayerNames[1]} />}*/}
    </div>
  )
}

export default PlayControls
